import { useEffect, useRef } from 'react';
import { messagesAPI } from '../utils/crmApi';
import { useCRM } from '../contexts/CRMContext';
import { useNotifications } from '../contexts/NotificationContext';
import toast from 'react-hot-toast';

// Global message tracking storage (shared across all instances)
export const globalMessageTracking = {
    lastMessageIds: new Map(), // conversationId -> Set of message IDs
    conversations: [],
    selectedConversationId: null // Currently selected/viewed conversation ID
};

/**
 * Global message polling hook that runs regardless of MessageList component state
 * This ensures polling continues even when the messages page is not visible
 */
export const useMessagePolling = () => {
    const { user, currentAgency } = useCRM();
    const { createNotification } = useNotifications();
    const pollingIntervalRef = useRef(null);
    const conversationsPollingIntervalRef = useRef(null);
    const userRef = useRef(user);
    const agencyRef = useRef(currentAgency);
    const createNotificationRef = useRef(createNotification);
    const missingContextLoggedRef = useRef(false);

    // Keep refs up to date
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        agencyRef.current = currentAgency;
    }, [currentAgency]);

    useEffect(() => {
        createNotificationRef.current = createNotification;
    }, [createNotification]);

    const userId = user?._id;
    const agencyId = currentAgency?._id;
    const hasMessagingContext = Boolean(userId && agencyId);

    // Set up polling
    useEffect(() => {
        if (!hasMessagingContext) {
            if (process.env.NODE_ENV === 'development' && !missingContextLoggedRef.current) {
                console.log('[useMessagePolling] Not setting up polling - missing user or agency');
            }
            missingContextLoggedRef.current = true;
            return;
        }

        missingContextLoggedRef.current = false;

        const activeUser = userRef.current;
        const activeAgency = agencyRef.current;

        if (!activeUser || !activeAgency) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[useMessagePolling] Context became unavailable before initialization');
            }
            return;
        }

        // Determine if user is an admin/agent for this agency
        const membership = activeUser.agencyMemberships?.find(
            m => String(m.agencyId) === String(activeAgency._id) || String(m.agencyId?._id) === String(activeAgency._id)
        );
        const isAdminView = activeUser.isSuperadmin || (membership && membership.role !== 'client');

        // Determine clientId - null for admin/agent, user._id for client
        const clientId = isAdminView ? null : activeUser._id;

        // Debug logging (only in development)
        if (process.env.NODE_ENV === 'development') {
            console.log('[useMessagePolling] Setting up global message polling');
            console.log('[useMessagePolling] Polling config:', { clientId, isAdminView, userId: activeUser._id });
        }

        // Load messages for a conversation (defined inside useEffect to access fresh user/notification)
        const loadMessages = async (conversationId, silent = false) => {
            try {
                // Extract client ID from conversation ID if needed
                // conversationId format: conv_<clientId>
                let targetClientId = clientId;
                if (conversationId && conversationId.startsWith('conv_')) {
                    const extractedClientId = conversationId.replace('conv_', '');
                    // For agency users (admin view), use the extracted client ID for accurate message fetching
                    if (isAdminView) {
                        targetClientId = extractedClientId;
                    } else {
                        // For clients, use the provided clientId (which is their own ID)
                        targetClientId = clientId || extractedClientId;
                    }
                }
                // Debug logging (only in development)
                if (process.env.NODE_ENV === 'development') {
                    console.log('[useMessagePolling] Loading messages for conversation:', {
                        conversationId,
                        clientId,
                        targetClientId,
                        isAdminView
                    });
                }
                const res = await messagesAPI.getMessages(conversationId, { clientId: targetClientId });
                if (res.data?.success) {
                    const newMessages = res.data.data || [];

                    // Get or initialize tracked message IDs for this conversation
                    if (!globalMessageTracking.lastMessageIds.has(conversationId)) {
                        globalMessageTracking.lastMessageIds.set(conversationId, new Set());
                    }
                    const trackedIds = globalMessageTracking.lastMessageIds.get(conversationId);

                    // Detect new messages (only when polling, not initial load)
                    // Debug logging (only in development)
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[useMessagePolling] Checking for new messages:', {
                            conversationId,
                            silent,
                            newMessagesCount: newMessages.length,
                            trackedIdsSize: trackedIds.size,
                            willCheck: !silent && newMessages.length > 0 && trackedIds.size > 0
                        });
                    }
                    
                    if (!silent && newMessages.length > 0 && trackedIds.size > 0) {
                        const currentMessageIds = new Set(newMessages.map(m => m._id));
                        const newMessageIds = [...currentMessageIds].filter(id => !trackedIds.has(id));

                        // Debug logging (only in development)
                        if (process.env.NODE_ENV === 'development') {
                            console.log('[useMessagePolling] New message IDs found:', {
                                conversationId,
                                newMessageIdsCount: newMessageIds.length,
                                newMessageIds,
                                trackedIds: Array.from(trackedIds)
                            });
                        }

                        if (newMessageIds.length > 0) {
                            // Find the actual new messages
                            const actualNewMessages = newMessages.filter(m => newMessageIds.includes(m._id));

                            // Get current user and notification function from refs
                            const currentUser = userRef.current;
                            const currentCreateNotification = createNotificationRef.current;

                            // Debug logging (only in development)
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] Processing new messages for notifications:', {
                                    conversationId,
                                    newMessagesCount: actualNewMessages.length,
                                    isAdminView,
                                    currentUserId: currentUser?._id,
                                    hasNotificationFunction: !!currentCreateNotification?.info
                                });
                            }

                            // Notify for messages not sent by current user
                            actualNewMessages.forEach(message => {
                                const getId = (value) => {
                                    if (!value) return null;
                                    if (typeof value === 'string') return value;
                                    if (value._id) return value._id.toString();
                                    if (value.toString) return value.toString();
                                    return null;
                                };

                                // Determine if message is from current user
                                let isCurrentUser = false;
                                if (isAdminView) {
                                    // For agency users, check if senderId matches current user
                                    // senderId is the actual sender (CRMUser ID), not agentId (assigned agent)
                                    // Note: message.senderId may be populated (object) or just an ID (string/ObjectId)
                                    const messageSenderId = getId(message.senderId);
                                    const currentUserId = getId(currentUser?._id);
                                    // Message is from current user if it's an agent message and senderId matches
                                    // Normalize both IDs to strings for reliable comparison
                                    const senderIdStr = messageSenderId ? String(messageSenderId) : null;
                                    const userIdStr = currentUserId ? String(currentUserId) : null;
                                    isCurrentUser = message.senderType === 'agent' && senderIdStr === userIdStr && senderIdStr !== null;
                                    
                                    // Debug logging (only in development)
                                    if (process.env.NODE_ENV === 'development') {
                                        console.log('[useMessagePolling] Agency user check:', {
                                            conversationId,
                                            messageId: message._id,
                                            messageSenderType: message.senderType,
                                            messageSenderId,
                                            messageAgentId: getId(message.agentId), // For reference
                                            currentUserId,
                                            isCurrentUser,
                                            senderIdsMatch: messageSenderId === currentUserId,
                                            messageData: {
                                                senderType: message.senderType,
                                                senderId: message.senderId,
                                                senderIdType: typeof message.senderId,
                                                senderIdId: message.senderId?._id,
                                                agentId: message.agentId
                                            }
                                        });
                                    }
                                } else {
                                    // For clients, check if senderId matches current user
                                    // Note: message.senderId may be populated (object) or just an ID (string/ObjectId)
                                    const messageSenderId = getId(message.senderId);
                                    const currentUserId = getId(currentUser?._id);
                                    // Normalize both IDs to strings for reliable comparison
                                    const senderIdStr = messageSenderId ? String(messageSenderId) : null;
                                    const userIdStr = currentUserId ? String(currentUserId) : null;
                                    // Message is from current user if it's a client message and senderId matches
                                    isCurrentUser = message.senderType === 'client' && senderIdStr === userIdStr && senderIdStr !== null;
                                    
                                    // Debug logging (only in development)
                                    if (process.env.NODE_ENV === 'development') {
                                        console.log('[useMessagePolling] Client user check:', {
                                            conversationId,
                                            messageId: message._id,
                                            messageSenderType: message.senderType,
                                            messageSenderId,
                                            currentUserId,
                                            isCurrentUser,
                                            senderIdsMatch: messageSenderId === currentUserId,
                                            messageData: {
                                                senderType: message.senderType,
                                                senderId: message.senderId,
                                                senderIdType: typeof message.senderId,
                                                senderIdId: message.senderId?._id
                                            }
                                        });
                                    }
                                }

                                // Check if user is currently viewing this conversation
                                const isViewingConversation = globalMessageTracking.selectedConversationId === conversationId;
                                
                                // Debug logging (only in development)
                                if (process.env.NODE_ENV === 'development') {
                                    const messageSenderId = getId(message.senderId);
                                    const currentUserId = getId(currentUser?._id);
                                    console.log('[useMessagePolling] Notification decision:', {
                                        conversationId,
                                        messageId: message._id,
                                        messageSenderType: message.senderType,
                                        messageSenderId,
                                        currentUserId,
                                        senderIdStr: messageSenderId ? String(messageSenderId) : null,
                                        userIdStr: currentUserId ? String(currentUserId) : null,
                                        idsMatch: messageSenderId && currentUserId ? String(messageSenderId) === String(currentUserId) : false,
                                        isCurrentUser,
                                        isViewingConversation,
                                        selectedConversationId: globalMessageTracking.selectedConversationId,
                                        willNotify: !isCurrentUser && !isViewingConversation
                                    });
                                }
                                
                                // Only notify if not from current user AND user is not viewing the conversation
                                if (!isCurrentUser && !isViewingConversation) {
                                    try {
                                        const conversation = globalMessageTracking.conversations.find(c => c.id === conversationId);
                                        let senderName = '';
                                        if (isAdminView) {
                                            senderName = message.senderType === 'client'
                                                ? conversation?.otherParticipant?.name || message.senderName || message.senderId?.name || 'Client'
                                                : message.senderName || message.senderId?.name || 'Team Member';
                                        } else {
                                            senderName = message.senderType === 'agent'
                                                ? conversation?.otherParticipant?.name || message.senderName || message.senderId?.name || 'Team Member'
                                                : message.senderName || message.senderId?.name || 'Client';
                                        }

                                        const notificationTitle = `New message from ${senderName}`;
                                        const notificationMessage = message.content?.substring(0, 100) + (message.content?.length > 100 ? '...' : '') || 'New message';

                                        // Debug logging (only in development)
                                        if (process.env.NODE_ENV === 'development') {
                                            console.log('[useMessagePolling] Creating notification:', {
                                                conversationId,
                                                messageId: message._id,
                                                notificationTitle,
                                                notificationMessage,
                                                hasNotificationFunction: !!currentCreateNotification?.info
                                            });
                                        }

                                        if (currentCreateNotification?.info) {
                                            // Extract client ID from conversation ID for navigation
                                            const convClientId = conversationId?.replace('conv_', '') || null;
                                            
                                            currentCreateNotification.info(
                                                notificationTitle,
                                                notificationMessage,
                                                {
                                                    type: 'client',
                                                    onClick: () => {
                                                        // Navigate to client detail page and open messages tab with conversation selected
                                                        console.log('[useMessagePolling] Notification onClick called', { convClientId, conversationId });
                                                        if (convClientId) {
                                                            // Navigate to client detail page with messages tab and conversation ID
                                                            const url = `/clients/${convClientId}?tab=messages&conversation=${conversationId}`;
                                                            console.log('[useMessagePolling] Navigating to:', url);
                                                            window.location.href = url;
                                                        } else {
                                                            // Fallback: navigate to clients list
                                                            console.log('[useMessagePolling] No client ID, navigating to clients list');
                                                            window.location.href = `/clients`;
                                                        }
                                                    }
                                                }
                                            );
                                            if (process.env.NODE_ENV === 'development') {
                                                console.log('[useMessagePolling] Notification created successfully');
                                            }
                                        } else {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.warn('[useMessagePolling] createNotification.info is not available');
                                            }
                                        }

                                        // Also show a toast
                                        toast.success(notificationTitle, {
                                            icon: '💬',
                                        });
                                    } catch (error) {
                                        console.error('[useMessagePolling] Error creating notification:', error);
                                    }
                                } else {
                                    if (process.env.NODE_ENV === 'development') {
                                        const reason = isCurrentUser ? 'message is from current user' : 'user is viewing conversation';
                                        console.log(`[useMessagePolling] Skipping notification - ${reason}`, {
                                            conversationId,
                                            messageId: message._id,
                                            isCurrentUser,
                                            isViewingConversation
                                        });
                                    }
                                }
                            });

                            // Update tracked message IDs
                            trackedIds.clear();
                            newMessages.forEach(m => trackedIds.add(m._id));
                        } else {
                            // No new messages, but update tracking
                            trackedIds.clear();
                            newMessages.forEach(m => trackedIds.add(m._id));
                        }
                    } else {
                        // Silent mode or first load - just update tracking
                        trackedIds.clear();
                        newMessages.forEach(m => trackedIds.add(m._id));
                    }
                }
            } catch (error) {
                console.error('[useMessagePolling] Failed to load messages:', error);
            }
        };

        // Load conversations
        const loadConversations = async () => {
            try {
                // Only log in development and only occasionally to reduce noise
                const shouldLog = Math.random() < 0.05; // 5% chance
                if (process.env.NODE_ENV === 'development' && shouldLog) {
                    console.log('[useMessagePolling] Loading conversations');
                }
                const res = await messagesAPI.getConversations(clientId);
                // Only log if there's an error or significant change
                if (process.env.NODE_ENV === 'development' && shouldLog && !res.data?.success) {
                    console.warn('[useMessagePolling] Failed to load conversations:', res.data);
                }
                if (res.data?.success) {
                    const loadedConversations = res.data.data || [];
                    const existingConversations = globalMessageTracking.conversations || [];
                    
                    // NEVER overwrite existing conversations with an empty array from API
                    // This prevents overwriting conversations that MessageList loaded
                    if (loadedConversations.length > 0) {
                        // API returned conversations - merge with existing ones (avoid duplicates)
                        const existingIds = new Set(existingConversations.map(c => c.id));
                        const newConversations = loadedConversations.filter(c => !existingIds.has(c.id));
                        
                        if (newConversations.length > 0) {
                            // Merge new conversations with existing ones
                            globalMessageTracking.conversations = [...existingConversations, ...newConversations];
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] Merged', newConversations.length, 'new conversations with', existingConversations.length, 'existing ones. Total:', globalMessageTracking.conversations.length);
                            }
                        } else if (existingConversations.length === 0) {
                            // No existing conversations, use API results
                            globalMessageTracking.conversations = loadedConversations;
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] Updated global tracking with', loadedConversations.length, 'conversations from API');
                            }
                        } else {
                            // API returned conversations but they're all duplicates - keep existing
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] API returned', loadedConversations.length, 'conversations but they already exist. Keeping', existingConversations.length, 'existing ones');
                            }
                        }
                    } else {
                        // API returned 0 conversations
                        if (existingConversations.length > 0) {
                            // Keep existing conversations from MessageList
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] API returned 0 conversations, preserving', existingConversations.length, 'conversations from MessageList');
                                console.log('[useMessagePolling] Preserved conversations:', existingConversations.map(c => ({ id: c.id, name: c.otherParticipant?.name })));
                            }
                        } else {
                            // No conversations anywhere - this is fine initially
                            globalMessageTracking.conversations = [];
                            if (process.env.NODE_ENV === 'development') {
                                console.log('[useMessagePolling] API returned 0 conversations, global tracking is also empty (initial state)');
                            }
                        }
                    }
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[useMessagePolling] Final global conversations count:', globalMessageTracking.conversations.length);
                    }

                    // Initialize tracking for all conversations in global tracking (not just API response)
                    // This ensures we track conversations loaded by MessageList even if API returns 0
                    const conversationsToTrack = globalMessageTracking.conversations.length > 0 
                        ? globalMessageTracking.conversations 
                        : loadedConversations;
                    
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[useMessagePolling] Initializing tracking for', conversationsToTrack.length, 'conversations');
                    }
                    conversationsToTrack.forEach((conv) => {
                        if (conv?.id && !globalMessageTracking.lastMessageIds.has(conv.id)) {
                            globalMessageTracking.lastMessageIds.set(conv.id, new Set());
                            // Load messages silently once to initialize tracking
                            loadMessages(conv.id, true).catch(() => {
                                // Silently fail - this is just for initial tracking setup
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('[useMessagePolling] Failed to load conversations:', error);
            }
        };

        // Poll messages for all conversations
        const pollMessages = () => {
            // Always read fresh from global tracking (not from closure)
            // This ensures we get conversations loaded by MessageList even if API returns 0
            // IMPORTANT: Read directly from globalMessageTracking.conversations to get latest value
            const currentConversations = Array.isArray(globalMessageTracking.conversations) 
                ? globalMessageTracking.conversations.slice() // Create a shallow copy
                : [];
            
            // Double-check by reading again (in case it was updated during the check)
            const finalConversations = Array.isArray(globalMessageTracking.conversations)
                ? globalMessageTracking.conversations.slice()
                : [];
            
            // Only log if there are conversations to poll (reduce noise)
            if (finalConversations.length === 0) {
                // Only log occasionally to avoid spam (every 10th check or so)
                const shouldLog = Math.random() < 0.1; // 10% chance
                if (process.env.NODE_ENV === 'development' && shouldLog) {
                    console.log('[useMessagePolling] No conversations to poll');
                }
                // Give MessageList a chance by checking one more time after a short delay
                // But don't block - let the interval continue
                setTimeout(() => {
                    const delayedCheck = Array.isArray(globalMessageTracking.conversations)
                        ? globalMessageTracking.conversations.slice()
                        : [];
                    if (delayedCheck.length > 0) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('[useMessagePolling] Found', delayedCheck.length, 'conversations on delayed check - polling now');
                        }
                        delayedCheck.forEach(conv => {
                            if (conv?.id) {
                                loadMessages(conv.id, false).catch(err => {
                                    console.error(`[useMessagePolling] Failed to poll messages for conversation ${conv.id}:`, err);
                                });
                            }
                        });
                    }
                }, 1000); // Check again after 1 second
                
                // Still try to reload from API
                loadConversations();
                return;
            }

            // Only log occasionally to reduce noise
            const shouldLog = Math.random() < 0.1; // 10% chance
            if (process.env.NODE_ENV === 'development' && shouldLog && finalConversations.length > 0) {
                console.log('[useMessagePolling] Polling', finalConversations.length, 'conversation(s)');
            }
            finalConversations.forEach(conv => {
                if (conv?.id) {
                    // Don't log individual conversation polling - too verbose
                    // Load messages with silent=false to enable notifications
                    loadMessages(conv.id, false).catch(err => {
                        console.error(`[useMessagePolling] Failed to poll messages for conversation ${conv.id}:`, err);
                    });
                }
            });
        };

        // Load conversations initially
        loadConversations();

        // Poll conversations list every 30 seconds
        conversationsPollingIntervalRef.current = setInterval(() => {
            // Don't log every poll - too verbose
            loadConversations();
        }, 30000);

        // Poll messages every 10 seconds (after initial delay)
        const initialPollTimeout = setTimeout(() => {
            if (process.env.NODE_ENV === 'development') {
                console.log('[useMessagePolling] Message polling initialized');
            }
            pollMessages();
            
            pollingIntervalRef.current = setInterval(() => {
                // Don't log every poll - too verbose
                pollMessages();
            }, 10000);
        }, 3000); // Wait 3 seconds for initial tracking to complete

        return () => {
                if (process.env.NODE_ENV === 'development') {
                console.log('[useMessagePolling] Cleaning up polling intervals');
            }
            clearTimeout(initialPollTimeout);
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (conversationsPollingIntervalRef.current) {
                clearInterval(conversationsPollingIntervalRef.current);
            }
        };
    }, [hasMessagingContext, userId, agencyId]); // Re-run when messaging context changes

    return {
        loadConversations: () => {},
        pollMessages: () => {}
    };
};
